export const STABLECOINS = ["USDC", "USDT", "DAI", "EURC", "PYUSD"] as const;
export const FIAT = ["USD", "EUR", "GBP", "MXN"] as const;
export const CHAINS = ["ethereum", "base", "polygon", "solana", "tron"] as const;

/**
 * Which stablecoins can actually be CONVERTED (fiat ↔ crypto orders) on each
 * chain. Asset ≠ chain: not every stablecoin is issued everywhere (USDT has no
 * base issuance), and DAI/EURC/PYUSD have no USD order pair at Conduit at all.
 * ethereum/base entries verified against live POST /orders (2026-07-17); the
 * rest reflect real-world issuance — Conduit's order validation is the final
 * word either way ("unsupported asset/chain combination").
 */
export const CONVERTIBLE_ASSETS: Record<string, readonly string[]> = {
  ethereum: ["USDC", "USDT"],
  base: ["USDC"],
  polygon: ["USDC", "USDT"],
  solana: ["USDC", "USDT"],
  tron: ["USDT"],
};

/**
 * Balance fields come back from the live API as AssetAmount objects
 * ({code, amount}) even where docs say plain strings — unwrap either shape.
 */
export function amountOf(v: unknown): string | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object" && "amount" in v) return String((v as { amount: unknown }).amount);
  return undefined;
}

export function codeOf(v: unknown): string | undefined {
  if (v && typeof v === "object" && "code" in v) return String((v as { code: unknown }).code);
  return undefined;
}

export function fmtAmount(amount?: unknown, code?: string) {
  const raw = amountOf(amount);
  if (raw === undefined) return "—";
  const n = Number(raw);
  if (Number.isNaN(n)) return raw;
  const digits = code && ["JPY"].includes(code) ? 0 : 2;
  return n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: 6 });
}

export function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function fmtDateTime(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export function shortId(id?: string | null, keep = 8) {
  if (!id) return "—";
  return id.length <= keep + 4 ? id : `${id.slice(0, keep)}…${id.slice(-4)}`;
}

/** RFI statuses that still need a response — shared by the page list and the nav badge. */
export const RFI_ACTIONABLE_STATUSES = ["open", "published", "awaiting_response", "pending"];

/* ---------- Transaction display helpers ----------
   Live transactions carry amounts on source/destination.assetAmount
   (no top-level assetAmount), and sender details under source.sender. */

type LooseAmount = { code?: string; chain?: string | null; amount?: string };
type LooseTxn = {
  type?: string;
  assetAmount?: LooseAmount;
  source?: {
    type?: string;
    sender?: { name?: string; accountNumber?: string };
    address?: string | null;
    assetAmount?: LooseAmount;
  };
  destination?: {
    type?: string;
    virtualAccountId?: string;
    address?: string | null;
    wireReceive?: { rail?: string; bankName?: string; accountLast4?: string; routingNumber?: string };
    // Real shape for outbound payouts (GET /transactions/{id}) — nested
    // under `recipient`, distinct from the deposit-side `wireReceive` above.
    recipient?: {
      rail?: string; // "us" | "swift" — the family only; the exact network
      // (fedwire/rtp/fednow) chosen at creation isn't echoed back here.
      legalName?: string;
      bankName?: string;
      accountNumber?: string;
      iban?: string;
      bic?: string;
    };
    assetAmount?: LooseAmount;
  };
};

/** Best displayable amount for a transaction, wherever the API put it. */
export function txnAsset(t: LooseTxn): LooseAmount {
  return t.assetAmount ?? t.destination?.assetAmount ?? t.source?.assetAmount ?? {};
}

/**
 * Map each deposit channel's receiving-account last4 → channel label
 * ("domestic · fedwire", "international · swift"). Incoming deposits only
 * carry rail family ("us"), but the receiving account identifies the channel.
 */
export function depositChannels(
  vas: Array<{ depositInstructions?: Array<{ type?: string; accountNumber?: string; rails?: Array<{ rail?: string }> }> }>,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const va of vas) {
    for (const di of va.depositInstructions ?? []) {
      if (!di.accountNumber) continue;
      const rails = (di.rails ?? []).map((r) => r.rail).filter(Boolean).join("/");
      const label =
        di.type === "swift_usd"
          ? "international · swift"
          : rails
            ? `domestic · ${rails}`
            : (di.type ?? "").replaceAll("_", " ");
      map[di.accountNumber.slice(-4)] = label;
    }
  }
  return map;
}

/** Human counterparty line: who sent it / where it went. */
export function txnParty(t: LooseTxn, channels?: Record<string, string>): string {
  if (t.type === "deposit") {
    const s = t.source?.sender;
    const from = s?.name ?? (t.source?.address ? shortId(t.source.address, 10) : "unknown sender");
    const acct = s?.accountNumber ? ` ····${s.accountNumber.slice(-4)}` : "";
    const via = t.destination?.wireReceive?.bankName ? ` → ${t.destination.wireReceive.bankName}` : "";
    const last4 = t.destination?.wireReceive?.accountLast4;
    const channel = last4 && channels?.[last4] ? ` · ${channels[last4]}` : "";
    return `from ${from}${acct}${via}${channel}`;
  }
  if (t.type === "withdrawal") {
    const r = t.destination?.recipient;
    if (r?.legalName) {
      const via = r.bankName ? ` → ${r.bankName}` : "";
      return `to ${r.legalName}${via}`;
    }
    const to = t.destination?.address;
    return to ? `to ${shortId(to, 10)}` : "outbound";
  }
  const src = t.source?.assetAmount?.code;
  const dst = t.destination?.assetAmount?.code;
  return src && dst && src !== dst ? `${src} → ${dst}` : "";
}

/** Payment rail label for any transaction type, for list display and filtering. */
export function txnRail(t: LooseTxn, channels?: Record<string, string>): string {
  if (t.type === "deposit") {
    const last4 = t.destination?.wireReceive?.accountLast4;
    if (last4 && channels?.[last4]) return channels[last4];
    const rail = t.destination?.wireReceive?.rail;
    return rail ? `domestic · ${rail}` : "—";
  }
  if (t.type === "withdrawal") return txnDestination(t).rail;
  const chain = txnAsset(t).chain;
  return chain ? `crypto · ${chain}` : "—";
}

/** Collapse a rail label ("domestic · fedwire", "crypto · base", …) into its filterable family. */
export function railFamily(rail: string): "domestic" | "international" | "crypto" | "other" {
  if (rail.startsWith("domestic")) return "domestic";
  if (rail.startsWith("international")) return "international";
  if (rail.startsWith("crypto")) return "crypto";
  return "other";
}

/** Recipient identity + payment rail for an outbound payout, for list display. */
export function txnDestination(t: LooseTxn): { name: string; rail: string; account?: string } {
  if (t.type !== "withdrawal") return { name: "—", rail: "—" };
  const r = t.destination?.recipient;
  if (r?.legalName) {
    const railLabel = r.rail === "swift" ? "international · swift" : r.rail === "us" ? "domestic · us" : (r.rail ?? "—");
    const account = r.iban || r.accountNumber ? `····${(r.iban || r.accountNumber || "").slice(-4)}` : undefined;
    return { name: r.bankName ? `${r.legalName} · ${r.bankName}` : r.legalName, rail: railLabel, account };
  }
  const addr = t.destination?.address;
  if (addr) {
    const chain = txnAsset(t).chain;
    return { name: shortId(addr, 10), rail: chain ? `crypto · ${chain}` : "crypto", account: addr };
  }
  return { name: "—", rail: "—" };
}

/**
 * Plain-language gloss for a transaction/payout status. Conduit's API
 * exposes only the state name — no ETA, no processing stage — so this is
 * the most a UI can honestly tell someone about what's happening.
 */
export function statusExplainer(status?: string | null): string {
  switch ((status ?? "").toLowerCase()) {
    case "pending":
      return "Accepted by Conduit and queued. Nothing further is needed from you unless a signature request or RFI appears below. International wires commonly take 1–3 business days to clear the receiving bank's correspondent chain — Conduit does not report intermediate hops, so this stays \"pending\" until it settles or fails.";
    case "processing":
      return "Actively clearing through the payment network or broadcasting on-chain. Usually the last step before completion.";
    case "awaiting_sender_information":
      return "A crypto deposit arrived from an unregistered address. Conduit needs the sender's details before it can post to your balance.";
    case "awaiting_user_signature":
      return "Waiting on wallet signers to approve before this can broadcast.";
    case "completed":
      return "Settled — funds have arrived at their destination.";
    case "failed":
      return "This did not complete. See the failure reason for why.";
    case "cancelled":
      return "Cancelled before it settled. No funds moved.";
    case "approved":
      return "Approved.";
    case "rejected":
      return "Rejected.";
    default:
      return "";
  }
}

/** Status → pill class per the flat green/charcoal system */
export function statusPill(status?: string | null): string {
  const s = (status ?? "").toLowerCase();
  if (["approved", "active", "completed", "succeeded", "registered", "paid", "positive"].includes(s)) return "pill pill-green";
  if (["rejected", "failed", "suspended", "error"].includes(s)) return "pill pill-danger";
  if (["processing", "pending", "pending_activation", "pending_review", "pending_screening", "paying", "awaiting_user_signature", "awaiting_sender_information", "provisioning", "activating", "open"].includes(s)) return "pill pill-dark";
  return "pill pill-cream";
}
